import React, { useState } from "react";
import {
  Box,
  Paper,
  Typography,
  Button,
  Card,
  CardContent,
  Chip,
  LinearProgress,
} from "@mui/material";
import {
  Science,
  NavigateNext,
  NavigateBefore,
  PlayArrow,
  Lightbulb,
  SkipNext,
} from "@mui/icons-material";
import { useDashboard } from "../../../contexts/DashboardContext";

interface TutorialStep {
  id: number;
  title: string;
  description: string;
  tip: string;
}

const tutorialSteps: TutorialStep[] = [
  {
    id: 1,
    title: 'Responsabilidade e Qualificação',
    description: 'Este procedimento deve ser executado por médico veterinário, técnico ou analista treinado em coleta e manipulação de amostras clínicas.',
    tip: 'Importante: O produtor pode executar este procedimento desde que tenha recebido treinamento adequado e conte com auxílio técnico especializado. A qualificação é essencial para garantir a qualidade dos resultados.',
  },
  {
    id: 2,
    title: 'Preparação do Animal',
    description: 'Identifique corretamente o animal e o teto a ser coletado antes de iniciar o procedimento.',
    tip: 'Identificação: Anote o número/nome do animal e especifique qual teto será coletado: Dianteiro Direito (DD), Dianteiro Esquerdo (DE), Traseiro Direito (TD) ou Traseiro Esquerdo (TE). Esta informação será crucial para o diagnóstico.',
  },
  {
    id: 3,
    title: 'Higienização do Teto',
    description: 'Realize a desinfecção adequada do teto seguindo a sequência correta.',
    tip: 'Procedimento: Utilize solução desinfetante nos tetos, sempre do mais distante ao mais próximo. Deixe secar ao ar livre (nunca sopre ou toque). Se necessário, seque com papel toalha estéril. A higienização adequada é fundamental para evitar contaminações.',
  },
  {
    id: 4,
    title: 'Descarte do Primeiro Jato',
    description: 'Despreze os três primeiros jatos de leite de cada teto antes da coleta.',
    tip: 'Importante: Este procedimento remove possíveis contaminações do canal do teto e garante que a amostra seja representativa do leite da glândula mamária. Nunca colete os primeiros jatos.',
  },
  {
    id: 5,
    title: 'Coleta da Amostra',
    description: 'Colete aproximadamente 10 mL de leite diretamente em frasco estéril.',
    tip: 'Procedimento: Colete o leite diretamente no frasco estéril, evitando contato com superfícies externas. Feche imediatamente após a coleta. Identifique com: número do animal, teto coletado, data e hora da coleta.',
  },
  {
    id: 6,
    title: 'Transporte da Amostra',
    description: 'Mantenha as amostras refrigeradas durante o transporte até o laboratório.',
    tip: 'Conservação: Mantenha entre 2°C e 8°C em caixa térmica. NUNCA congele, pois pode causar lise bacteriana. O tempo máximo entre coleta e semeadura deve ser de no máximo 12 horas para garantir a viabilidade dos microrganismos.',
  },
  {
    id: 7,
    title: 'Recebimento no Laboratório',
    description: 'Verifique a identificação e integridade da amostra antes do processamento.',
    tip: 'Verificação: Confirme se a identificação está legível e completa. Verifique se o frasco está íntegro e sem vazamentos. Homogeneize suavemente por inversão antes de abrir (não agite vigorosamente).',
  },
  {
    id: 8,
    title: 'Preparação para Semeadura',
    description: 'Prepare adequadamente a placa e a amostra antes da inoculação.',
    tip: 'Preparação: Deixe a placa de Agar Cromast dentro da estufa por pelo menos 1 hora para secar o líquido evaporado. Deixe a amostra de leite atingir temperatura ambiente antes da semeadura. Esta etapa é crucial para o sucesso do cultivo.',
  },
  {
    id: 9,
    title: 'Técnica de Semeadura',
    description: 'Realize a inoculação da amostra no Agar Cromast seguindo técnica asséptica.',
    tip: 'Procedimento: Use técnica asséptica em bancada limpa ou cabine de segurança biológica. Com alça estéril, inocule uma alíquota de leite no Agar Cromast. Espalhe em estrias sucessivas para isolamento de colônias. Mantenha esterilidade durante todo o processo.',
  },
  {
    id: 10,
    title: 'Incubação e Leitura',
    description: 'Incube as placas nas condições adequadas e realize a leitura dos resultados.',
    tip: 'Condições: Incube a 35-37°C por 44-48 horas. Após este período, observe o crescimento de colônias, suas características morfológicas e coloração. O Agar Cromast permite identificação presuntiva dos microrganismos pela cor das colônias.',
  },
];

const MastitisTutorial: React.FC = () => {
  const { updateContent } = useDashboard();
  const [currentStep, setCurrentStep] = useState(0);

  const handleNext = () => {
    if (currentStep < tutorialSteps.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleStartTest = () => {
    updateContent("mastitisTeatStep");
  };

  const handleSkipTutorial = () => {
    updateContent("mastitisTeatStep");
  };

  const handleCancel = () => {
    updateContent("welcome");
  };

  const currentTutorialStep = tutorialSteps[currentStep];
  const progress = ((currentStep + 1) / tutorialSteps.length) * 100;

  return (
    <Box
      sx={{ height: "100%", display: "flex", flexDirection: "column", p: 2 }}
    >
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Science sx={{ color: "#8B5CF6" }} />
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            Tutorial - Diagnóstico Microbiológico
          </Typography>
        </Box>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button
            variant="contained"
            color="primary"
            size="small"
            startIcon={<SkipNext />}
            onClick={handleSkipTutorial}
            sx={{
              textTransform: "none",
              fontWeight: 600,
              boxShadow: 2,
              "&:hover": {
                boxShadow: 4,
                transform: "translateY(-1px)",
              },
            }}
          >
            Pular Tutorial
          </Button>
          <Button
            variant="text"
            size="small"
            onClick={handleCancel}
            sx={{ color: "text.secondary", textTransform: "none" }}
          >
            Cancelar
          </Button>
        </Box>
      </Box>

      {/* Progress Bar */}
      <Box sx={{ mb: 3 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 1,
          }}
        >
          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            Etapa {currentStep + 1} de {tutorialSteps.length}
          </Typography>
          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            {Math.round(progress)}% concluído
          </Typography>
        </Box>
        <LinearProgress
          variant="determinate"
          value={progress}
          sx={{ height: 8, borderRadius: 4 }}
        />
      </Box>

      {/* Main Content */}
      <Box
        sx={{
          flex: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Paper
          elevation={0}
          sx={{
            maxWidth: 800,
            width: "100%",
            p: 4,
            textAlign: "center",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 3,
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 3,
          }}
        >
          {/* Step Number Badge */}
          <Chip
            label={`Etapa ${currentStep + 1}`}
            color="primary"
            variant="outlined"
            sx={{ fontSize: "0.875rem", fontWeight: 600 }}
          />

          {/* Step Title */}
          <Typography
            variant="h4"
            sx={{ fontWeight: 700, color: "primary.main" }}
          >
            {currentTutorialStep.title}
          </Typography>

          {/* Step Description */}
          <Typography
            variant="body1"
            sx={{
              color: "text.primary",
              maxWidth: 600,
              lineHeight: 1.6,
              fontSize: "1.1rem",
            }}
          >
            {currentTutorialStep.description}
          </Typography>

          {/* Tip Card */}
          <Card
            sx={{
              maxWidth: 600,
              width: "100%",
              backgroundColor: "action.hover",
              border: "1px solid",
              borderColor: "warning.light",
            }}
          >
            <CardContent sx={{ p: 2 }}>
              <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
                <Lightbulb sx={{ color: "warning.main", mt: 0.25 }} />
                <Box>
                  <Typography
                    variant="subtitle2"
                    sx={{ fontWeight: 600, color: "warning.dark", mb: 0.5 }}
                  >
                    Dica:
                  </Typography>
                  <Typography variant="body2" sx={{ color: "text.secondary" }}>
                    {currentTutorialStep.tip}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>

          {/* Navigation Buttons */}
          <Box sx={{ display: "flex", gap: 2, mt: 2 }}>
            <Button
              variant="outlined"
              startIcon={<NavigateBefore />}
              onClick={handlePrevious}
              disabled={currentStep === 0}
              sx={{ minWidth: 120 }}
            >
              Anterior
            </Button>

            {currentStep === tutorialSteps.length - 1 ? (
              <Button
                variant="contained"
                startIcon={<PlayArrow />}
                onClick={handleStartTest}
                sx={{ minWidth: 160 }}
              >
                Iniciar Teste
              </Button>
            ) : (
              <Button
                variant="contained"
                endIcon={<NavigateNext />}
                onClick={handleNext}
                sx={{ minWidth: 120 }}
              >
                Próximo
              </Button>
            )}
          </Box>
        </Paper>
      </Box>
    </Box>
  );
};

export default MastitisTutorial;
